/**
 * Several open tables at once.
 *
 * Specification: ../test/checkbook.spec.ts. Master plan §4 M4, §4 M3.
 *
 * A `PosSession` is ONE order. That is the right shape — an order is the
 * aggregate, and one fold per order is what keeps convergence provable — but a
 * till holds a dozen of them at a time, and until now the POS held exactly one,
 * opened implicitly against no table at all.
 *
 * This is the book of open checks: one session per table, created on demand,
 * retired when the check settles.
 *
 * THEY SHARE ONE OUTBOX. Each order is its own event stream, so the streams
 * interleave freely — but the outbox is a blocking FIFO over the whole device
 * precisely because it must be: a void on table 4 must never overtake the sale
 * it reverses just because table 7 was quiet.
 */

import { PosSession, type CheckView, type MenuSnapshot, type PosSessionDeps } from "./session";
import type { ConsumptionMode, OrderEvent } from "@gf/module-orders";

/** A table as the floor plan shows it, with whatever is open on it. */
export interface FloorEntry {
  readonly tableRef: string;
  readonly name: string;
  readonly seats: number;
  readonly area: string;
  readonly occupied: boolean;
  /** Outstanding balance, or null when nothing is open. */
  readonly outstandingMinor: bigint | null;
  readonly lineCount: number;
  /** Anything rung and not yet sent to a station. */
  readonly unfiredCount: number;
  readonly openedAt?: Date | null;
  readonly openedBy?: string | null;
}

export interface FloorTableSpec {
  readonly tableRef: string;
  readonly name: string;
  readonly seats: number;
  readonly area: string;
}

export type SessionFactory = (deps: Pick<PosSessionDeps, "menu">) => PosSession;

/**
 * A second order on a table that already has one is held under
 * `<table>+<orderId>`: a key on this till only, never on the wire.
 */
const APART = "+";
/** A counter sale's reference: `pickup:` and its number. */
export const PICKUP = "pickup:";

export const isHeldApart = (ref: string): boolean => ref.includes(APART) && !ref.startsWith("tab:");
export const tableOf = (ref: string): string => ref.split(APART)[0]!;

export class CheckBook {
  readonly #sessions = new Map<string, PosSession>();
  readonly #create: SessionFactory;
  #menu: MenuSnapshot;

  constructor(menu: MenuSnapshot, create: SessionFactory) {
    this.#menu = menu;
    this.#create = create;
  }

  /**
   * Take delivery of a newer menu.
   *
   * Offered to EVERY open session, because each one decides for itself whether
   * to stage it: a session with an open check holds the new version back so a
   * line already rung cannot reprice under the person holding the handheld,
   * and applies it when the check settles. A book that swapped the menu
   * wholesale would reprice a dozen checks at once, silently.
   */
  offerMenu(menu: MenuSnapshot): void {
    this.#menu = menu;
    for (const session of this.#sessions.values()) session.offerMenu(menu);
  }

  /**
   * The session for a table, opening one if the table is free — `seat` when
   * the guests come from a booking: their covers, and the booking to mark.
   */
  async open(
    tableRef: string,
    consumptionMode: ConsumptionMode = "DINE_IN",
    seat?: { readonly covers: number; readonly reservationRef: string },
  ): Promise<PosSession> {
    const existing = this.#sessions.get(tableRef);
    if (existing) return existing;
    // A check held apart that has just gone: never an order on "T1+…".
    if (isHeldApart(tableRef)) return this.open(tableOf(tableRef), consumptionMode, seat);

    const session = this.#create({ menu: this.#menu });
    await session.openOrder({ tableRef, consumptionMode, ...(seat ?? {}) });
    this.#sessions.set(tableRef, session);
    return session;
  }

  /**
   * A sale at the counter, under its number: `pickup:A07`. Here or to go is
   * chosen first, so the order is priced in that mode from the first item.
   */
  async openCounter(pickupCode: string, consumptionMode: ConsumptionMode): Promise<PosSession> {
    const tableRef = `pickup:${pickupCode}`;
    const existing = this.#sessions.get(tableRef);
    if (existing) return existing;
    const session = this.#create({ menu: this.#menu });
    await session.openOrder({ tableRef, consumptionMode, orderType: "COUNTER", pickupCode });
    this.#sessions.set(tableRef, session);
    return session;
  }

  /** Counter sales still open — rung and not yet paid — oldest first. */
  counterSales(): readonly FloorEntry[] {
    return [...this.#sessions.entries()]
      .filter(([ref, session]) => ref.startsWith(PICKUP) && session.view().state === "OPEN")
      .map(([ref, session]) => {
        const view = session.view();
        return {
          tableRef: ref,
          name: ref.slice(PICKUP.length),
          seats: 0,
          area: "",
          occupied: true,
          outstandingMinor: view.totals.outstandingMinor,
          lineCount: view.lines.filter((l) => !l.voided).length,
          unfiredCount: view.unfiredCount,
          openedAt: view.openedAt ?? null,
          openedBy: view.openedBy ?? null,
        };
      })
      .sort((a, b) => (a.openedAt?.getTime() ?? 0) - (b.openedAt?.getTime() ?? 0));
  }

  /**
   * Take a table the SERVER already has open.
   *
   * WITHOUT THIS THE TILL FORGETS THE FLOOR EVERY TIME IT RELOADS. The book is
   * a `Map` in one browser tab; a refresh, a tablet waking up, a browser
   * updating itself — any of them and every open table is gone. Tapping the
   * table again then opened a SECOND order on it, so a venue accumulated
   * phantom checks nobody could see or settle, and the floor plan read "0 of 11
   * occupied" while four tables were being served.
   *
   * Found by reloading the till twice during a walkthrough and asking the
   * server what it thought: four open orders on table 1, three of them empty.
   *
   * The session is built from the stream rather than from a summary, because a
   * till has to be able to add to the check, void from it and settle it — all
   * of which need the events, not a total.
   */
  async adopt(tableRef: string, events: readonly OrderEvent[]): Promise<PosSession | null> {
    if (events.length === 0) return null;

    /**
     * A TABLE ALREADY IN THE BOOK IS MERGED INTO, not skipped.
     *
     * This used to return the existing session untouched, which was right when
     * adoption happened once at startup from a single source. It stopped being
     * right when the till learned to restore its own floor from a local journal
     * first and then read the server: the server's copy arrived second and was
     * dropped on the floor, so a round a guest ordered from their phone during
     * the outage never appeared on the till that was about to tender the table.
     *
     * `sync` is convergent — it merges by event id and folds with
     * `foldOrderConverging` — so calling it with events this session already
     * has is a no-op, and calling it with events it has not is the point.
     */
    const orderId = (events.find((e) => e.type === "order.opened")?.payload as { orderId?: string } | undefined)
      ?.orderId;

    // TWO PARTIES ON ONE TABLE — two devices opened it. A different order
    // already there keeps it; this one is held apart, never folded into it.
    const heldAt = orderId ? this.#refOf(orderId) : undefined;
    const occupant = this.#sessions.get(tableRef)?.view().orderId;
    const target = orderId && occupant && occupant !== orderId ? `${tableRef}${APART}${orderId}` : tableRef;

    // A party another till moved, or a table that has since freed: the order
    // follows, rather than being held twice.
    if (heldAt !== undefined && heldAt !== target) {
      const held = this.#sessions.get(heldAt)!;
      this.#sessions.delete(heldAt);
      this.#sessions.set(target, held);
    }
    const existing = this.#sessions.get(target);
    if (existing) {
      await existing.sync(events);
      // Settled somewhere else — another till, or a guest paying from the
      // table. The book must let go of it or the floor shows it occupied.
      if (existing.view().state !== "OPEN") {
        this.#sessions.delete(target);
        return null;
      }
      return existing;
    }

    const session = this.#create({ menu: this.#menu });
    await session.sync(events);

    // A settled check is not adopted: the table is free, and putting it in the
    // book would show it occupied until somebody retired it by hand.
    if (session.view().state !== "OPEN") return null;

    this.#sessions.set(target, session);
    return session;
  }

  get(tableRef: string): PosSession | undefined {
    return this.#sessions.get(tableRef);
  }

  /**
   * Retire a settled check.
   *
   * Refuses to retire one that has not settled. Losing an open check is losing
   * the money on it: the events are already in the outbox, but the till would
   * show the table as free and a server would seat somebody on top of an
   * unpaid bill.
   */
  retire(tableRef: string): boolean {
    const session = this.#sessions.get(tableRef);
    if (!session) return false;
    if (session.view().state !== "SETTLED") return false;

    this.#sessions.delete(tableRef);
    return true;
  }

  /**
   * Drop a table a MORE AUTHORITATIVE source says is no longer open.
   *
   * Deliberately separate from `retire`, which refuses an unsettled check —
   * that refusal is the protection against a till losing a live bill, and this
   * must not become a way around it.
   *
   * THE CALLER'S CONTRACT, which this cannot enforce and which is the whole of
   * its safety: only for a table restored from a CACHED floor and since absent
   * from the server's own list. Such a check was settled on another device
   * while this till was away, and the events proving it are on the server. A
   * table this till rang itself must never be passed here — its events may
   * still be sitting in the outbox, unseen by the server through no fault of
   * the check, and forgetting it would lose a bill somebody is about to pay.
   */
  forget(tableRef: string): boolean {
    return this.#sessions.delete(tableRef);
  }

  /** `forget`, by the order rather than the table — a table can hold two. */
  forgetOrder(orderId: string): boolean {
    const ref = this.#refOf(orderId);
    return ref !== undefined && this.#sessions.delete(ref);
  }

  #refOf(orderId: string): string | undefined {
    for (const [ref, session] of this.#sessions) if (session.view().orderId === orderId) return ref;
    return undefined;
  }

  /**
   * Move a party to another table. Refused onto a table that is already open:
   * two parties' checks on one table is a merge, and a merge is a decision.
   */
  async transfer(fromTableRef: string, toTableRef: string): Promise<boolean> {
    const session = this.#sessions.get(fromTableRef);
    if (!session || this.#sessions.has(toTableRef)) return false;
    const moved = await session.transferTable(toTableRef);
    if (!moved.ok) return false;
    this.#sessions.delete(fromTableRef);
    this.#sessions.set(toTableRef, session);
    return true;
  }

  /** Let go of a table opened by mistake. The session refuses one with anything on it. */
  async abandon(tableRef: string): Promise<boolean> {
    const session = this.#sessions.get(tableRef);
    if (!session) return false;
    const result = await session.abandon();
    if (!result.ok) return false;
    this.#sessions.delete(tableRef);
    return true;
  }

  /** Every open check, for a supervisor view or a shift close. */
  openChecks(): readonly CheckView[] {
    return [...this.#sessions.values()].map((s) => s.view());
  }

  /**
   * The floor, with what is open on it.
   *
   * Computed rather than stored. A separate "is this table busy" flag is a
   * second source of truth about money, and the two disagree the first time a
   * check settles during a render.
   */
  floor(tables: readonly FloorTableSpec[]): readonly FloorEntry[] {
    return tables.map((table) => {
      const view = this.#sessions.get(table.tableRef)?.view();
      const open = view !== undefined && view.state === "OPEN";

      return {
        ...table,
        occupied: open,
        outstandingMinor: open ? view.totals.outstandingMinor : null,
        lineCount: open ? view.lines.filter((l) => !l.voided).length : 0,
        unfiredCount: open ? view.unfiredCount : 0,
        openedAt: open ? (view.openedAt ?? null) : null,
        openedBy: open ? (view.openedBy ?? null) : null,
      };
    });
  }

  /**
   * Open checks on no table in the floor plan: bar tabs, oldest first.
   * `name` is how a person says it — "Účet 3" — from the caller's words.
   */
  tabs(
    tables: readonly FloorTableSpec[],
    label: (n: number) => string,
    apart: (table: string) => string = (table) => table,
    /** Every table's name, including those not on the floor shown (tables switched off). */
    names: ReadonlyMap<string, string> = new Map(),
  ): readonly FloorEntry[] {
    const onFloor = new Map(tables.map((t) => [t.tableRef, t.name]));
    const nameOf = (ref: string): string => onFloor.get(ref) ?? names.get(ref) ?? ref;
    return [...this.#sessions.entries()]
      // A counter sale is not a tab: it is the counter's, listed by `counterSales`.
      .filter(([ref, session]) => !onFloor.has(ref) && !ref.startsWith(PICKUP) && session.view().state === "OPEN")
      .map(([ref, session]) => {
        const view = session.view();
        return {
          tableRef: ref,
          name: isHeldApart(ref)
            ? apart(nameOf(tableOf(ref)))
            : ref.startsWith("tab:")
              ? label(Number(ref.replace(/^tab:/, "")) || 0)
              : nameOf(ref),
          seats: 0,
          area: "",
          occupied: true,
          outstandingMinor: view.totals.outstandingMinor,
          lineCount: view.lines.filter((l) => !l.voided).length,
          unfiredCount: view.unfiredCount,
          openedAt: view.openedAt ?? null,
          openedBy: view.openedBy ?? null,
        };
      })
      .sort((a, b) => (a.openedAt?.getTime() ?? 0) - (b.openedAt?.getTime() ?? 0));
  }

  /** The next free tab reference: "tab:1", "tab:2"… never one still open. */
  nextTabRef(): string {
    for (let n = 1; ; n++) {
      const ref = `tab:${n}`;
      if (!this.#sessions.has(ref)) return ref;
    }
  }

  /**
   * Tables holding something rung but never sent.
   *
   * The end-of-shift question a manager actually asks. An unfired line is food
   * nobody is cooking and money nobody has been asked for, and it is invisible
   * on a floor plan that only shows totals.
   */
  tablesWithUnfired(): readonly string[] {
    return [...this.#sessions.entries()]
      .filter(([, session]) => session.view().unfiredCount > 0)
      .map(([tableRef]) => tableRef);
  }
}
